import moment from 'moment';

import { DatosPago, MedioPagoReq } from '../types/Pago/types';
import { PetForm_3 } from '../types/Pet/types';

/**
 * Validador de Algoritmo de Luhn (Tarjetas de crédito, IMEI, etc.)
 * Mejora: Corrige el bug del 0 y elimina manipulaciones de arrays innecesarias.
 */
const luhnValidation = (num: string): boolean => {
  // 1. Verificamos que solo sean números
  if (!/^\d+$/.test(num)) return false;

  let sum = 0;
  let shouldDouble = false; // Empezamos desde el final, el último dígito (check digit) no se duplica

  // 2. Iteramos de atrás hacia adelante (es más eficiente que split + reverse)
  for (let i = num.length - 1; i >= 0; i--) {
    let digit = parseInt(num.charAt(i), 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

/**
 * Validador de CBU Argentina
 * Mejora: Usa función auxiliar para no repetir lógica y arrays de pesos.
 */
const cbuValidation = (cbu: string): boolean => {
  if (!cbu || cbu.length !== 22 || !/^\d+$/.test(cbu)) {
    return false;
  }

  // Pesos definidos por el estándar bancario argentino
  const weightsBlock1 = [7, 1, 3, 9, 7, 1, 3];
  const weightsBlock2 = [3, 9, 7, 1, 3, 9, 7, 1, 3, 9, 7, 1, 3];

  const block1 = cbu.slice(0, 8);
  const block2 = cbu.slice(8, 22);

  // Validamos ambos bloques reutilizando la lógica
  const isBlock1Valid = validateCbuBlock(block1, weightsBlock1);
  const isBlock2Valid = validateCbuBlock(block2, weightsBlock2);

  return isBlock1Valid && isBlock2Valid;
};

/**
 * Función auxiliar para validar bloques de CBU
 */
const validateCbuBlock = (block: string, weights: number[]): boolean => {
  // El último dígito del bloque es el verificador
  const verifier = parseInt(block[block.length - 1], 10);
  const digits = block.slice(0, -1); // Los dígitos a calcular

  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    sum += parseInt(digits[i], 10) * weights[i];
  }

  // Lógica de dígito verificador: 10 - (suma % 10). Si es 10, el dígito es 0.
  const diff = 10 - (sum % 10);
  const calculatedVerifier = diff === 10 ? 0 : diff;

  return calculatedVerifier === verifier;
};

const normalizeNumeroPago = (value?: string) => value?.replace(/\s+/g, '') ?? '';

const normalizeVencimiento = (value?: string): string | null => {
  if (!value) return null;
  const [mm, yy] = value.split('/');
  if (!mm || !yy) return null;
  return `20${yy}/${mm}/01`;
};

const isExpirationValid = (value?: string): boolean => {
  if (!value) return true;

  // MM/YY
  const expiration = moment(value, 'MM/YY', true);

  if (!expiration.isValid()) return false;

  // Las tarjetas vencen al final del mes
  const endOfMonth = expiration.endOf('month');

  return endOfMonth.isSameOrAfter(moment(), 'day');
};

const normalizePaymentDate = (value?: string | null): string | null =>
  value ? moment(value).format('YYYY/MM/DD') : null;

const isDuplicatedMedioPago = (existing: DatosPago, incoming: MedioPagoReq) => {
  const sameNumber = normalizeNumeroPago(existing.numero_pago) === incoming.numero_pago;
  const sameType = existing.medioPago?.id ? existing.medioPago.id === incoming.tipo : true;
  const sameExpiration = incoming.vencimiento
    ? normalizePaymentDate(existing.vencimiento) === incoming.vencimiento
    : true;

  return sameNumber && sameType && sameExpiration;
};

/**
 * Formatea número de tarjeta:
 * - Visa / Master / etc → 4-4-4-4
 * - American Express → 4-6-5
 */
const formatNumeroPago = (value: string): string => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');

  // American Express: empieza con 34 o 37 y tiene 15 dígitos
  const isAmex = /^3[47]/.test(digits);

  if (isAmex) {
    const part1 = digits.slice(0, 4);
    const part2 = digits.slice(4, 10);
    const part3 = digits.slice(10, 15);

    return [part1, part2, part3].filter(Boolean).join(' ');
  }

  // Default: 4-4-4-4 (hasta 16)
  return (
    digits
      .slice(0, 16)
      .match(/.{1,4}/g)
      ?.join(' ') ?? ''
  );
};

const detectCardType = (numero?: string, tipo?: string): string => {
  if (tipo) {
    const t = tipo.toUpperCase();
    if (t.includes('VISA')) return 'Visa';
    if (t.includes('MASTER')) return 'MasterCard';
    if (/AMEX|AMERICAN/.test(t)) return 'Amex';
  }

  if (!numero) return 'Unknown';

  const digits = numero.replace(/\D/g, '');
  if (digits.startsWith('4')) return 'Visa';
  if (/^[52]/.test(digits)) return 'MasterCard';
  if (/^3[47]/.test(digits)) return 'Amex';

  return 'Unknown';
};

const getDefaultsByMetodo = (metodo: 'card' | 'cbu'): Partial<PetForm_3> =>
  metodo === 'card'
    ? {
        nombreTitular: '',
        numeroTarjeta: '',
        vencimiento: '',
      }
    : {
        cbu: '',
      };

/**
 * Determina si un tipo de medio de pago corresponde a CBU
 * @param tipo - El tipo de medio de pago (ej: "VISA", "CBU Caja de Ahorro", "CBU Cuenta Corriente")
 * @param verifica - Opcional: el campo verifica del medioPago (ej: "cbu", "luhn")
 */
const isTipoCBU = (tipo: string, verifica?: string): boolean => {
  if (verifica) return verifica.toLowerCase() === 'cbu';
  return tipo.toLowerCase().includes('cbu');
};

export {
  luhnValidation,
  cbuValidation,
  normalizeNumeroPago,
  normalizeVencimiento,
  isExpirationValid,
  isDuplicatedMedioPago,
  formatNumeroPago,
  detectCardType,
  getDefaultsByMetodo,
  isTipoCBU,
};
